import { useState } from 'react';

import { Major, AppIcon, ColorToken } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { getMajorUnderEmploymentRating } from '@backrs_ui/grow-ui/src/pages/Majors/majors.utils';

import { Box, Typography, SxProps, Theme } from '@mui/material';

const STRINGS = appStrings.modules.majors.careerOutcomes;

const SWITCH_ICON_PROPS = {
  iconName: 'SwitchHorizontal01' as const,
  diameter: '20px' as const,
  color: 'Colors/Text/text-secondary' as const,
};

const createTextShadow = (color: string): string => {
  const offsets = [
    [0, 0, 0.5],
    [0, 0, 0.5],
    [-1, -1, 0],
    [1, -1, 0],
    [-1, 1, 0],
    [1, 1, 0],
    [-2, -2, 0],
    [2, -2, 0],
    [-2, 2, 0],
    [2, 2, 0],
    [-1.5, -1.5, 0],
    [1.5, -1.5, 0],
    [-1.5, 1.5, 0],
    [1.5, 1.5, 0],
    [-1, 0, 0],
    [1, 0, 0],
    [0, -1, 0],
    [0, 1, 0],
    [-2, 0, 0],
    [2, 0, 0],
    [0, -2, 0],
    [0, 2, 0],
    [-0.5, -2, 0],
    [0.5, -2, 0],
    [-0.5, 2, 0],
    [0.5, 2, 0],
    [-1, -2, 0],
    [1, -2, 0],
    [-1, 2, 0],
    [1, 2, 0],
    [-1.5, -2, 0],
    [1.5, -2, 0],
    [-1.5, 2, 0],
    [1.5, 2, 0],
    [-2, -0.5, 0],
    [2, -0.5, 0],
    [-2, 0.5, 0],
    [2, 0.5, 0],
    [-2, -1, 0],
    [2, -1, 0],
    [-2, 1, 0],
    [2, 1, 0],
    [-2, -1.5, 0],
    [2, -1.5, 0],
    [-2, 1.5, 0],
    [2, 1.5, 0],
  ];

  return offsets.map(([x, y, blur]) => `${x}px ${y}px ${blur}px ${color}`).join(', ');
};

type UnderemploymentCardProps = Readonly<{
  major: Major;
}>;

export function UnderemploymentCard({ major }: UnderemploymentCardProps) {
  const [activeRate, setActiveRate] = useState<'underEmployment' | 'success'>('underEmployment');
  // const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const { rating, iconColor, icon } = getMajorUnderEmploymentRating(major);
  const underEmploymentRate = major.underEmployment;
  const successRate = 1 - major.underEmployment;
  const underEmploymentLabel = STRINGS.underEmployment;
  const descriptionText = STRINGS.underEmploymentInfo(major.underEmploymentRationale).join(' ');
  // const tooltipText = major.underEmploymentRationale;

  const currentPercentage =
    activeRate === 'underEmployment' ? underEmploymentRate * 100 : successRate * 100;

  const percentageColor = activeRate === 'underEmployment' ? 'Error/500' : 'Brand/500';

  const isUnderEmploymentActive = activeRate === 'underEmployment';

  const handleSwitch = () => {
    setActiveRate(activeRate === 'underEmployment' ? 'success' : 'underEmployment');
  };

  const renderSwitchLabel = (
    label: string,
    rate: 'underEmployment' | 'success',
    isActive: boolean,
  ) => (
    <Typography
      variant={isActive ? 'text-sm-semibold' : 'text-sm-regular'}
      sx={isActive ? activeLabelSx : inactiveLabelSx}
      onClick={() => setActiveRate(rate)}
    >
      {label}
    </Typography>
  );

  return (
    <Box sx={cardContainerSx}>
      {/* Header Section */}
      <Box sx={headerContainerSx}>
        {/* Switchable Labels */}
        <Box sx={switchLabelsContainerSx}>
          {isUnderEmploymentActive ? (
            <>
              {renderSwitchLabel(underEmploymentLabel, 'underEmployment', true)}
              <Box sx={switchIconContainerSx} onClick={handleSwitch}>
                <AppIcon
                  iconName={SWITCH_ICON_PROPS.iconName}
                  diameter={SWITCH_ICON_PROPS.diameter}
                  color={SWITCH_ICON_PROPS.color}
                />
              </Box>
              {renderSwitchLabel(STRINGS.successRate, 'success', false)}
            </>
          ) : (
            <>
              {renderSwitchLabel(STRINGS.successRate, 'success', true)}
              <Box sx={switchIconContainerSx} onClick={handleSwitch}>
                <AppIcon
                  iconName={SWITCH_ICON_PROPS.iconName}
                  diameter={SWITCH_ICON_PROPS.diameter}
                  color={SWITCH_ICON_PROPS.color}
                />
              </Box>
              {renderSwitchLabel(underEmploymentLabel, 'underEmployment', false)}
            </>
          )}
        </Box>

        {/* Info Icon - TODO: ADD BACK WHEN WE HAVE THE INFO TEXT */}
        {/* <ClickAwayListener onClickAway={() => setIsTooltipOpen(false)}>
          <Tooltip
            open={isTooltipOpen}
            onClose={() => setIsTooltipOpen(false)}
            title={tooltipText}
            placement="top"
            arrow
            disableFocusListener
            disableHoverListener
            disableTouchListener
            componentsProps={{
              tooltip: {
                sx: {
                  whiteSpace: 'pre-line',
                  padding: '16px',
                },
              },
            }}
          >
            <Box sx={infoIconContainerSx} onClick={() => setIsTooltipOpen((prev) => !prev)}>
              <AppIcon iconName="InfoCircle" diameter="20px" color="Colors/Foreground/fg-quinary" />
            </Box>
          </Tooltip>
        </ClickAwayListener> */}
      </Box>

      {/* Main Content Section */}
      <Box sx={contentContainerSx}>
        {/* Left Section: Rating and Description */}
        <Box sx={leftSectionSx}>
          <Box sx={ratingContainerSx}>
            <Typography variant="display-sm-semibold">{rating}</Typography>
            <Box sx={ratingIconBadgeSx}>
              <AppIcon iconName={icon} diameter="12px" color={iconColor} />
            </Box>
          </Box>
          <Typography variant="text-sm-medium" color={({ backrs }) => backrs.colors['Beige/950']}>
            {descriptionText}
          </Typography>
        </Box>

        {/* Right Section: Large Percentage */}
        <Box sx={percentageContainerSx}>
          <Typography variant="display-lg-semibold" sx={percentageTextSx(percentageColor)}>
            {currentPercentage.toFixed(0)}%
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

const cardContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  borderRadius: backrs.radius['radius-xl'],
  backgroundColor: backrs.colors['Colors/Base/White'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  boxShadow: backrs.shadows['shadow-xs'],
  flex: '1 1 min(535px, 100%)',
  minWidth: 0,
});

const headerContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  px: backrs.spacing['spacing-2xl'],
  pt: backrs.spacing['spacing-lg'],
  pb: backrs.spacing['spacing-md'],
});

const switchLabelsContainerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const activeLabelSx: SxProps<Theme> = ({ backrs }) => ({
  cursor: 'pointer',
  '&:hover': {
    color: backrs.colors['Colors/Text/text-primary-hover'],
  },
});

const inactiveLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
  cursor: 'pointer',
  '&:hover': {
    color: backrs.colors['Colors/Text/text-secondary-hover'],
  },
});

const switchIconContainerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  cursor: 'pointer',
  '&:hover': {
    opacity: 0.8,
  },
};

// const infoIconContainerSx: SxProps<Theme> = {
//   cursor: 'pointer',
//   '&:hover': {
//     opacity: 0.8,
//   },
// };

const contentContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Gray/200']}`,
  borderLeft: 'none',
  borderRight: 'none',
  borderBottom: 'none',
  padding: backrs.spacing['spacing-2xl'],
});

const leftSectionSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const ratingContainerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const ratingIconBadgeSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Base/White'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  borderRadius: backrs.radius['radius-md'],
  padding: backrs.spacing['spacing-md'],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const percentageContainerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
};

const percentageTextSx =
  (color: ColorToken): SxProps<Theme> =>
  ({ backrs }) => ({
    color: backrs.colors[color],
    fontFamily: '"Irregardless Variable" !important',
    fontSize: '120px',
    fontStyle: 'normal',
    fontWeight: 800,
    lineHeight: '40px',
    letterSpacing: '2px',
    textShadow: createTextShadow(backrs.colors['Colors/Base/Black']),
  });
